<template>
  <div class="admin-asset-table min-h-screen bg-gray-50">
    <!-- Toolbar -->
    <AdminToolbar
      :title="config.title"
      :back-route="config.backRoute"
      :total-count="items.length"
      :filtered-count="filteredItems.length"
      :export-filename="config.exportFilename"
      :searchable-fields="config.searchableFields"
      @search="handleSearch"
      @export="handleExport"
      @import="() => showImportModal = true"
    />
    <!-- Bouton suppression multiple -->
    <div v-if="selectedUuids.length > 0" class="flex justify-end px-6 mt-2">
      <button
        class="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
        type="button"
        @click="showBulkDeleteModal = true"
      >
        Supprimer les éléments sélectionnés ({{ selectedUuids.length }})
      </button>
    </div>
    <!-- Contenu principal -->
    <div class="px-6 py-6">
      <!-- Banner d'information pour les non-admins -->
      <AdminPermissionBanner />
      <!-- Loading state -->
      <div v-if="loading" class="flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
      <!-- Tableau -->
      <div v-else class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <!-- En-têtes -->
            <thead class="bg-gray-50">
              <tr>
                <!-- Colonne Actions -->
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                  <input type="checkbox" :checked="allSelected" @change="toggleSelectAll" />
                </th>
                <!-- Colonnes de données -->
                <th v-for="column in config.columns" :key="`header-${column.key}`"
                  class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 last:border-r-0"
                  :class="{ 'cursor-pointer hover:bg-gray-100': column.sortable }"
                  @click="column.sortable ? handleSort(column.key) : null">
                  <div class="flex items-center gap-2">
                    <span>{{ column.label }}</span>
                    <div v-if="column.sortable" class="flex flex-col">
                      <ChevronUp class="w-3 h-3" :class="{ 'text-blue-600': sortKey === column.key && sortDirection === 'asc' }" />
                      <ChevronDown class="w-3 h-3 -mt-1" :class="{ 'text-blue-600': sortKey === column.key && sortDirection === 'desc' }" />
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="item in sortedItems" :key="item.uuid" class="transition-colors" :class="{
                'hover:bg-gray-50': !item.is_deleted,
                'bg-yellow-50 hover:bg-yellow-100 dark:bg-yellow-900/20': item.is_deleted,
              }">
                <!-- Case à cocher sélection -->
                <td class="px-4 py-3 whitespace-nowrap border-r border-gray-200">
                  <div class="flex items-center gap-2">
                    <input type="checkbox" :checked="selectedUuids.includes(item.uuid)" @change="toggleSelect(item.uuid)" />
                    <template v-if="!item.is_deleted">
                      <!-- Bouton de réinitialisation de mot de passe pour les utilisateurs -->
                      <button v-if="config.entityType === 'users' && canEdit" class="inline-flex items-center justify-center w-8 h-8 text-orange-600 hover:text-orange-800 hover:bg-orange-100 rounded-md transition-colors" :title="'Réinitialiser le mot de passe'" @click="handleResetPassword(item)"><KeyIcon class="w-4 h-4" /></button>
                      <button v-if="canDelete" class="inline-flex items-center justify-center w-8 h-8 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-md transition-colors" :title="'Supprimer (logique)'" @click="handleDelete(item)"><TrashIcon class="w-4 h-4" /></button>
                    </template>
                    <template v-else>
                      <button v-if="canRestore" class="inline-flex items-center justify-center w-8 h-8 text-green-600 hover:text-green-800 hover:bg-green-100 rounded-md transition-colors" :title="'Restaurer'" @click="handleRestore(item)"><ArrowUturnLeftIcon class="w-4 h-4" /></button>
                      <button v-if="canHardDelete" class="inline-flex items-center justify-center w-8 h-8 text-gray-700 hover:text-white hover:bg-red-600 rounded-md transition-colors" :title="'Supprimer définitivement'" @click="handleHardDelete(item)"><ArchiveBoxXMarkIcon class="w-4 h-4" /></button>
                    </template>
                  </div>
                </td>
                <!-- Données -->
                <td v-for="column in config.columns" :key="`${item.uuid}-${column.key}`" class="px-4 py-3 whitespace-nowrap border-r border-gray-200 last:border-r-0" :class="{
                  'cursor-pointer hover:bg-blue-50': canEdit && column.editable && column.type !== 'checkbox',
                  'text-gray-500': !column.editable || !canEdit
                }" :title="canEdit && column.editable && column.type !== 'checkbox' ? 'Cliquer pour éditer' : ''" @click="canEdit && column.editable && column.type !== 'checkbox' && editingCell !== `${item.uuid}-${column.key}` ? startEdit(item, column.key) : null">
                  <!-- Checkbox toujours visible si éditable -->
                  <template v-if="column.type === 'checkbox' && column.editable">
                    <div class="flex justify-center">
                      <input :checked="Boolean((item as unknown as Record<string, unknown>)[column.key])" type="checkbox" class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" :class="{ 'cursor-pointer': canEdit, 'cursor-not-allowed opacity-50': !canEdit }" :disabled="!canEdit" @change="canEdit ? toggleCheckbox(item, column.key) : null" />
                    </div>
                  </template>
                  <!-- Checkbox superadmin only -->
                  <template v-else-if="column.type === 'checkbox' && column.superadminOnly && isSuperAdmin">
                    <div class="flex justify-center">
                      <input :checked="Boolean((item as unknown as Record<string, unknown>)[column.key])" type="checkbox" class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer" @change="toggleCheckbox(item, column.key)" />
                    </div>
                  </template>
                  <!-- Checkbox non-éditable -->
                  <template v-else-if="column.type === 'checkbox' && !column.editable">
                    <div class="flex justify-center">
                      <input :checked="Boolean((item as unknown as Record<string, unknown>)[column.key])" type="checkbox" class="w-4 h-4 text-gray-400 border-gray-300 rounded focus:ring-transparent cursor-not-allowed" disabled />
                    </div>
                  </template>
                  <!-- Mode édition pour les autres types -->
                  <template v-else-if="editingCell === `${item.uuid}-${column.key}`">
                    <!-- Select utilisateur -->
                    <select v-if="column.type === 'select' && column.key === 'user_id'" v-model="editValue" class="w-full px-2 py-1 border border-blue-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" @change="saveEdit(item, column.key)" @keydown.enter.prevent="saveEdit(item, column.key)" @keydown.esc.prevent="cancelEdit" @mousedown.stop>
                      <option v-for="user in users" :key="user.uuid" :value="user.uuid">{{ user.name }}</option>
                    </select>
                    <!-- Select générique -->
                    <select v-else-if="column.type === 'select'" v-model="editValue" class="w-full px-2 py-1 border border-blue-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" @change="saveEdit(item, column.key)" @keydown.enter.prevent="saveEdit(item, column.key)" @keydown.esc.prevent="cancelEdit" @mousedown.stop>
                      <option v-for="option in column.options" :key="String(option.value)" :value="option.value">{{ option.label }}</option>
                    </select>
                    <!-- Number -->
                    <input v-else-if="column.type === 'number'" v-model.number="editValue" type="number" class="w-full px-2 py-1 border border-blue-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" @blur="saveEdit(item, column.key)" @keydown.enter.prevent="saveEdit(item, column.key)" @keydown.esc.prevent="cancelEdit" @mousedown.stop />
                    <!-- Textarea -->
                    <textarea v-else-if="column.type === 'textarea'" :value="String(editValue || '')" rows="1" class="w-full px-2 py-1 border border-blue-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" @input="editValue = ($event.target as HTMLTextAreaElement).value" @blur="saveEdit(item, column.key)" @keydown.esc.prevent="cancelEdit" @mousedown.stop />
                    <!-- Text par défaut -->
                    <input v-else v-model="editValue" type="text" class="w-full px-2 py-1 border border-blue-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" @blur="saveEdit(item, column.key)" @keydown.enter.prevent="saveEdit(item, column.key)" @keydown.esc.prevent="cancelEdit" @mousedown.stop />
                  </template>
                  <!-- Mode affichage pour les autres types -->
                  <template v-else>
                    <div class="text-sm text-gray-900 truncate max-w-xs">
                      {{ formatCellValue(item, column) }}
                    </div>
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <!-- Modal de confirmation de suppression multiple -->
    <ModalAreYouSure v-model="showBulkDeleteModal" title="Confirmer la suppression multiple" :message="`Êtes-vous sûr de vouloir supprimer ${selectedUuids.length} éléments ? Cette action est irréversible.`" confirm-text="Supprimer" cancel-text="Annuler" @confirm="confirmBulkDelete" @cancel="cancelBulkDelete" />
    <!-- Modal de confirmation de suppression -->
    <ModalAreYouSure v-model="showDeleteModal" title="Confirmer la suppression"
      :message="`Êtes-vous sûr de vouloir supprimer cet élément ? Cette action est irréversible.`"
      confirm-text="Supprimer" cancel-text="Annuler" @confirm="confirmDelete" @cancel="cancelDelete" />

    <!-- Modal de confirmation de restauration -->
    <ModalAreYouSure v-model="showRestoreModal" title="Confirmer la restauration"
      :message="`Êtes-vous sûr de vouloir restaurer l'élément ${itemToRestore?.uuid} ?`"
      confirm-text="Restaurer" cancel-text="Annuler" @confirm="confirmRestore" @cancel="cancelRestore" />

    <!-- Modal de confirmation de suppression définitive -->
    <ModalAreYouSure v-model="showHardDeleteModal" title="⚠️ SUPPRESSION DÉFINITIVE"
      :message="`ATTENTION: Suppression irréversible de l'élément ${itemToHardDelete?.uuid}.\n\nCette action ne peut pas être annulée. `"
      confirm-text="Supprimer définitivement" cancel-text="Annuler" @confirm="confirmHardDelete" @cancel="cancelHardDelete" />

    <!-- Modal d'import universel -->
    <AdminAssetImportModal
      v-model="showImportModal"
      :entity-label="config.title"
      :required-fields="config.requiredFields"
      :api-create="apiService.create"
      :existing-assets="items as unknown as Record<string, unknown>[]"
      @import-success="handleImportSuccess"
    />

    <!-- Modal de réinitialisation de mot de passe -->
    <AdminResetPasswordModal
      v-model="showResetPasswordModal"
      :user="userToResetPassword"
      @success="handleResetPasswordSuccess"
      @error="handleResetPasswordError"
    />

  <!-- Notifications -->
    <Notification ref="notification" />
  </div>
</template>

<script setup lang="ts">
import {
    Eraser as ArchiveBoxXMarkIcon,
    Undo2 as ArrowUturnLeftIcon,
    ChevronDown,
    ChevronUp,
    Key as KeyIcon,
    Trash as TrashIcon
} from 'lucide-vue-next';
import { computed, nextTick, onMounted, ref } from 'vue';
// import { useRouter } from 'vue-router';

import Notification from '@/components/Notification.vue';
import LoadingSpinner from '@/components/UI/LoadingSpinner.vue';
import ModalAreYouSure from '@/components/utils/modal_areyousure.vue';
import AdminAssetImportModal from './AdminAssetImportModal.vue';
import AdminPermissionBanner from './AdminPermissionBanner.vue';
import AdminResetPasswordModal from './AdminResetPasswordModal.vue';
import AdminToolbar from './AdminToolbar.vue';

import type { Entity } from '@/api/types/core/common';
import type { User } from '@/api/types/core/users';
import type { AdminAssetConfig, ColumnConfig } from '@/configs/adminAssetConfigs';
import { useAuthStore } from '@/stores/auth';
import {
    exportToCSV,
    exportToJSON,
    filterBySearch,
    formatDate,
    generateTempUUID,
    sortData
} from '@/utils/admin';


interface AdminAssetTableProps {
  config: AdminAssetConfig;
  items: Entity[];
  users: User[];
  apiService: {
    getAll: () => Promise<Entity[]>;
    create: (data: Record<string, unknown>) => Promise<Entity>;
    update: (uuid: string, data: Record<string, unknown>) => Promise<Entity>;
    delete: (uuid: string) => Promise<void>;
    restore?: (uuid: string) => Promise<void>;
    hardDelete?: (uuid: string) => Promise<void>;
  };
}

interface AdminAssetTableEmits {
  refresh: [];
  created: [item: Entity];
  updated: [item: Entity];
  deleted: [uuid: string];
}

const props = defineProps<AdminAssetTableProps>();
const emit = defineEmits<AdminAssetTableEmits>();

// const router = useRouter();
const authStore = useAuthStore();

// État local
const loading = ref(false);
const searchTerm = ref('');
const sortKey = ref('created_at');
const sortDirection = ref<'asc' | 'desc'>('desc');

// Édition inline
const editingCell = ref<string | null>(null);
const editValue = ref<unknown>(null);

// Création
const newItem = ref<Record<string, unknown>>({});
const validationErrors = ref<Record<string, string>>({});

// Suppression
const showDeleteModal = ref(false);
const itemToDelete = ref<Entity | null>(null);

// Restauration
const showRestoreModal = ref(false);
const itemToRestore = ref<Entity | null>(null);

// Suppression définitive
const showHardDeleteModal = ref(false);
const itemToHardDelete = ref<Entity | null>(null);

// Import
const showImportModal = ref(false);

// Réinitialisation de mot de passe
const showResetPasswordModal = ref(false);
const userToResetPassword = ref<User | null>(null);

// Sélection multiple
const selectedUuids = ref<string[]>([]);
const allSelected = computed(() => selectedUuids.value.length > 0 && selectedUuids.value.length === sortedItems.value.length);
const toggleSelect = (uuid: string): void => {
  if (selectedUuids.value.includes(uuid)) {
    selectedUuids.value = selectedUuids.value.filter(u => u !== uuid);
  } else {
    selectedUuids.value.push(uuid);
  }
};
const toggleSelectAll = (): void => {
  if (allSelected.value) {
    selectedUuids.value = [];
  } else {
    selectedUuids.value = sortedItems.value.map(item => item.uuid);
  }
};
// Suppression multiple
const showBulkDeleteModal = ref(false);
const confirmBulkDelete = async (): Promise<void> => {
  if (!canDelete.value || selectedUuids.value.length === 0) return;
  try {
    // On utilise sortedItems pour avoir accès à is_deleted
    const itemsByUuid = Object.fromEntries(sortedItems.value.map(item => [item.uuid, item]));
    for (const uuid of selectedUuids.value) {
      const item = itemsByUuid[uuid];
      if (item && item.is_deleted && props.apiService.hardDelete) {
        await props.apiService.hardDelete(uuid);
      } else {
        await props.apiService.delete(uuid);
      }
    }
    emit('refresh');
    showNotification(`${selectedUuids.value.length} éléments supprimés`, 'success');
    selectedUuids.value = [];
  } catch (error: unknown) {
    showNotification('Erreur lors de la suppression multiple', 'error');
  } finally {
    showBulkDeleteModal.value = false;
  }
};
const cancelBulkDelete = (): void => {
  showBulkDeleteModal.value = false;
};

// Notifications
const notification = ref();

// Computed
const filteredItems = computed((): Entity[] =>
  filterBySearch(props.items, searchTerm.value, props.config.searchableFields)
);

const sortedItems = computed((): Entity[] =>
  sortData(filteredItems.value, sortKey.value, sortDirection.value)
);

// Permissions basées sur le rôle utilisateur
const isAdmin = computed((): boolean => authStore.user?.role === 'admin' || authStore.user?.role === 'superadmin');
const isSuperAdmin = computed((): boolean => authStore.user?.role === 'superadmin');
const canEdit = computed((): boolean => isAdmin.value);
const canDelete = computed((): boolean => isAdmin.value);
const canRestore = computed((): boolean => isAdmin.value && !!props.apiService.restore);
const canHardDelete = computed((): boolean => isAdmin.value && !!props.apiService.hardDelete);

// Méthodes
const initNewItem = (): void => {
  const item: Record<string, unknown> = {
    uuid: generateTempUUID(),
    user_id: '',
    is_public: true,
    is_deleted: false
  };

  // Initialiser les champs selon leur type
  props.config.columns.forEach((column: ColumnConfig): void => {
    if (column.editable && column.type !== 'date') {
      switch (column.type) {
        case 'checkbox':
          item[column.key] = false;
          break;
        case 'number':
          item[column.key] = 0;
          break;
        default:
          item[column.key] = '';
      }
    }
  });

  newItem.value = item;
  validationErrors.value = {};
};

const handleSearch = (term: string): void => {
  searchTerm.value = term;
};

const handleSort = (key: string): void => {
  if (sortKey.value === key) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey.value = key;
    sortDirection.value = 'asc';
  }
};

// Correction des types explicites (no explicit any)
const handleExport = async (format: 'json' | 'csv'): Promise<void> => {
  try {
    if (format === 'json') {
      exportToJSON(filteredItems.value, props.config.exportFilename);
    } else {
      const columns = props.config.columns.map((col: ColumnConfig): string => col.key);
      exportToCSV(filteredItems.value, props.config.exportFilename, columns);
    }
    showNotification('Export réussi', 'success');
  } catch (error: unknown) {
    showNotification('Erreur lors de l\'export', 'error');
  }
};

const startEdit = (item: Entity, key: string): void => {
  editingCell.value = `${item.uuid}-${key}`;
  editValue.value = (item as unknown as Record<string, unknown>)[key];

  nextTick((): void => {
    const input = document.querySelector(`input, select, textarea`) as HTMLElement;
    input?.focus();
  });
};

const saveEdit = async (item: Entity, key: string): Promise<void> => {
  // Éviter les appels multiples
  if (!editingCell.value || editingCell.value !== `${item.uuid}-${key}`) {
    return;
  }

  // Vérifier les permissions
  if (!canEdit.value) {
    showNotification('Accès refusé : Seuls les administrateurs et super-administrateurs peuvent modifier des éléments', 'error');
    cancelEdit();
    return;
  }

  // Valider que la valeur n'est pas vide pour les champs requis
  const currentValue = editValue.value;
  if (currentValue === null || currentValue === undefined || currentValue === '') {
    // Si c'est un champ requis, ne pas sauvegarder
    const isRequired = props.config.requiredFields.includes(key);
    if (isRequired) {
      showNotification(`Le champ "${key}" est requis`, 'error');
      cancelEdit();
      return;
    }
  }

  try {
    // Créer une copie complète de l'item avec la modification
    const updatedData: Record<string, unknown> = {
      ...item as unknown as Record<string, unknown>,
      [key]: currentValue
    };

    // Supprimer les champs non nécessaires pour l'API
    delete updatedData.editing;
    delete updatedData.name; // Champ calculé côté client

    console.log('Sauvegarde:', { key, oldValue: (item as unknown as Record<string, unknown>)[key], newValue: currentValue });

    const updatedItem = await props.apiService.update(item.uuid, updatedData);
    emit('updated', updatedItem);
    emit('refresh');
    showNotification('Modification sauvegardée', 'success');
  } catch (error: unknown) {
    console.error('Erreur lors de la modification:', error);
    const errorMessage = getErrorMessage(error);
    showNotification(errorMessage, 'error');
  } finally {
    editingCell.value = null;
    editValue.value = null;
  }
};

const cancelEdit = (): void => {
  editingCell.value = null;
  editValue.value = null;
};

const toggleCheckbox = async (item: Entity, key: string): Promise<void> => {
  // Vérifier les permissions
  if (!canEdit.value) {
    showNotification('Accès refusé : Seuls les administrateurs et super-administrateurs peuvent modifier des éléments', 'error');
    return;
  }

  try {
    const currentValue = (item as unknown as Record<string, unknown>)[key];

    // Créer une copie complète de l'item avec la modification
    const updatedData: Record<string, unknown> = {
      ...item as unknown as Record<string, unknown>,
      [key]: !currentValue
    };

    // Supprimer les champs non nécessaires pour l'API
    delete updatedData.editing;
    delete updatedData.name; // Champ calculé côté client

    const updatedItem = await props.apiService.update(item.uuid, updatedData);
    emit('updated', updatedItem);
    emit('refresh');
    showNotification('Modification sauvegardée', 'success');
  } catch (error: unknown) {
    console.error('Erreur lors de la modification checkbox:', error);
    const errorMessage = getErrorMessage(error);
    showNotification(errorMessage, 'error');
  }
};



const handleDelete = async (item: Entity): Promise<void> => {
  itemToDelete.value = item;
  showDeleteModal.value = true;
};

const handleResetPassword = (item: Entity): void => {
  // Trouver l'utilisateur correspondant dans la liste des utilisateurs
  const user = props.users.find(u => u.uuid === item.uuid);
  if (user) {
    userToResetPassword.value = user;
    showResetPasswordModal.value = true;
  } else {
    showNotification('Utilisateur non trouvé', 'error');
  }
};

const confirmDelete = async (): Promise<void> => {
  if (!itemToDelete.value) return;

  // Vérifier les permissions
  if (!canDelete.value) {
    showNotification('Accès refusé : Seuls les administrateurs et super-administrateurs peuvent supprimer des éléments', 'error');
    showDeleteModal.value = false;
    itemToDelete.value = null;
    return;
  }

  try {
    await props.apiService.delete(itemToDelete.value.uuid);
    emit('deleted', itemToDelete.value.uuid);
    emit('refresh');
    showNotification('Élément supprimé avec succès', 'success');
  } catch (error: unknown) {
    console.error(`Erreur lors de la suppression de ${itemToDelete.value.uuid}:`, error);
    const errorMessage = getErrorMessage(error);
    showNotification(errorMessage, 'error');
  } finally {
    showDeleteModal.value = false;
    itemToDelete.value = null;
  }
};

const cancelDelete = (): void => {
  showDeleteModal.value = false;
  itemToDelete.value = null;
};

const confirmRestore = async (): Promise<void> => {
  if (!itemToRestore.value) return;

  // Vérifier les permissions
  if (!canDelete.value) {
    showNotification('Accès refusé : Seuls les administrateurs et super-administrateurs peuvent restaurer des éléments', 'error');
    showRestoreModal.value = false;
    itemToRestore.value = null;
    return;
  }

  try {
    if (!props.apiService.restore) {
      showNotification('La restauration n\'est pas disponible pour ce type d\'élément', 'error');
      return;
    }
    await props.apiService.restore(itemToRestore.value.uuid);
    emit('refresh');
    showNotification('Élément restauré avec succès', 'success');
  } catch (error: unknown) {
    console.error(`Erreur lors de la restauration de ${itemToRestore.value.uuid}:`, error);
    const errorMessage = getErrorMessage(error);
    showNotification(errorMessage, 'error');
  } finally {
    showRestoreModal.value = false;
    itemToRestore.value = null;
  }
};

const cancelRestore = (): void => {
  showRestoreModal.value = false;
  itemToRestore.value = null;
};

const confirmHardDelete = async (): Promise<void> => {
  if (!itemToHardDelete.value) return;

  // Vérifier les permissions
  if (!canDelete.value) {
    showNotification('Accès refusé : Seuls les administrateurs et super-administrateurs peuvent supprimer définitivement des éléments', 'error');
    showHardDeleteModal.value = false;
    itemToHardDelete.value = null;
    return;
  }

  try {
    if (!props.apiService.hardDelete) {
      showNotification('La suppression définitive n\'est pas disponible pour ce type d\'élément', 'error');
      return;
    }
    await props.apiService.hardDelete(itemToHardDelete.value.uuid);
    emit('refresh');
    showNotification('Élément supprimé définitivement', 'success');
  } catch (error: unknown) {
    console.error(`Erreur lors de la suppression définitive de ${itemToHardDelete.value.uuid}:`, error);
    const errorMessage = getErrorMessage(error);
    showNotification(errorMessage, 'error');
  } finally {
    showHardDeleteModal.value = false;
    itemToHardDelete.value = null;
  }
};

const cancelHardDelete = (): void => {
  showHardDeleteModal.value = false;
  itemToHardDelete.value = null;
};

const handleRestore = async (item: Entity): Promise<void> => {
  itemToRestore.value = item;
  showRestoreModal.value = true;
};

const handleHardDelete = async (item: Entity): Promise<void> => {
  itemToHardDelete.value = item;
  showHardDeleteModal.value = true;
};

const formatCellValue = (item: Entity, column: ColumnConfig): string => {
  const value = (item as unknown as Record<string, unknown>)[column.key];

  if (value == null) return '';

  if (column.formatter) {
    return column.formatter(value);
  }

  if (column.type === 'date') {
    return formatDate(String(value));
  }

  if (column.type === 'checkbox') {
    return value ? 'Oui' : 'Non';
  }

  if (column.key === 'user_id') {
    const user = props.users.find((u: User): boolean => u.uuid === value);
    return user?.name || 'Utilisateur inconnu';
  }

  return String(value);
};

const showNotification = (message: string, type: 'success' | 'error'): void => {
  notification.value?.showNotification(message, type);
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    // Vérifier si c'est une erreur HTTP 403 (Accès interdit)
    if (error.message.includes('403')) {
      return 'Accès refusé : Seuls les administrateurs et super-administrateurs peuvent effectuer cette action';
    }
    // Vérifier si c'est une erreur HTTP 401 (Non authentifié)
    if (error.message.includes('401')) {
      return 'Session expirée : Veuillez vous reconnecter';
    }
    return error.message;
  }
  return 'Une erreur inattendue s\'est produite';
};

function handleImportSuccess(): void {
  // On peut rafraîchir la liste ou afficher une notification
  emit('refresh');
  showNotification('Import terminé', 'success');
}

const handleResetPasswordSuccess = (user: User): void => {
  showNotification(`Mot de passe réinitialisé avec succès pour ${user.name}`, 'success');
  userToResetPassword.value = null;
};

const handleResetPasswordError = (error: string): void => {
  showNotification(`Erreur lors de la réinitialisation : ${error}`, 'error');
  userToResetPassword.value = null;
};

// Lifecycle
onMounted((): void => {
  initNewItem();
});
</script>

<style scoped>
/* Styles pour le tableau responsive */
.admin-asset-table table {
  table-layout: auto;
}

.admin-asset-table td {
  max-width: 200px;
}

.admin-asset-table .truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>